import {
  isShareTargetPickerAvailable,
  openShareTargetPicker,
  generateInviteURL,
  sendInviteURL,
} from 'src/lib/sharing';
import { GCInstant as platform } from 'src/lib/gcinstant';
import { makePayload } from 'src/lib/analytics';
import { Feature } from 'src/replicant/analytics';
import StateObserver, { user } from 'src/StateObserver';
import { AnalyticsProperties } from '@play-co/gcinstant';
import { createJankenChallengeFlexMessage } from 'src/replicant/chatbot/flex/janken';
import { getCurrentEvolution } from 'src/replicant/pet/pet.getters';
import {
  getPetAssetKey,
  speciesAssetsRatio,
  type SpeciesChatbotType,
} from 'src/replicant/chatbot/flex/species';
import { getPlayerProfile } from 'src/replicant/profile/profile.getters';
import { getCreativeText } from 'src/creatives/text';
import type { CreativeText } from 'src/replicant/creatives/text';
import { createJankenChallengeCreative } from './janken.creative';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import { withLoading } from 'src/lib/utils';
import i18n from 'src/lib/i18n/i18n';
import { captureGenericError } from 'src/lib/sentry';
import { track } from 'src/lib/analytics/utils';
import type { JankenMove } from 'src/replicant/janken/janken.ruleset';

export type JankenChallengeType = 'challenge' | 'rematch';

type JankenShareSubFeature =
  | typeof Feature.JANKEN.LINE_CHALLENGE
  | typeof Feature.JANKEN.LINE_REMATCH
  | typeof Feature.JANKEN.NATIVE_CHALLENGE
  | typeof Feature.JANKEN.NATIVE_REMATCH;

/** All $subFeature values used in janken share entry payloads. Reused by entry detection. */
export const JANKEN_SHARE_SUBFEATURES: ReadonlySet<string> = new Set<string>([
  Feature.JANKEN.LINE_CHALLENGE,
  Feature.JANKEN.LINE_REMATCH,
  Feature.JANKEN.NATIVE_CHALLENGE,
  Feature.JANKEN.NATIVE_REMATCH,
  // ─── Legacy ───
  // In-flight links from older clients still carry these old subFeature
  // strings — kept here so they continue to trigger the janken entry flow.
  Feature.JANKEN.LEGACY_LINE_CHALLENGE,
  Feature.JANKEN.LEGACY_LINE_REMATCH,
  Feature.JANKEN.LEGACY_NATIVE_CHALLENGE,
  Feature.JANKEN.LEGACY_NATIVE_REMATCH,
]);

const jankenShareSubFeatures = {
  line: {
    challenge: Feature.JANKEN.LINE_CHALLENGE,
    rematch: Feature.JANKEN.LINE_REMATCH,
  },
  native: {
    challenge: Feature.JANKEN.NATIVE_CHALLENGE,
    rematch: Feature.JANKEN.NATIVE_REMATCH,
  },
} as const;

/** Resolve the `$subFeature` for a janken share entry payload. */
function getJankenShareSubFeature(
  shareMethod: 'line' | 'native',
  challengeType: JankenChallengeType,
): JankenShareSubFeature {
  return jankenShareSubFeatures[shareMethod][challengeType];
}

/**
 * Share a janken challenge via LINE Share Target Picker (preferred) or native share (fallback).
 *
 * Tracks `JankenSend` with the creative properties that were actually used for sharing.
 * Each path (LINE / native) resolves its own creative, so repeated native shares for
 * the same challengeId report the cached creative rather than a fresh random one.
 *
 * @param opts.senderMove - The sender's derived move for this challenge
 * @param opts.challengeType - `'challenge'` for initial sends, `'rematch'` for post-result re-challenges
 * @param opts.forceNativeShare - If true, skip Share Target Picker and use native share directly
 */
export async function shareJankenChallenge(
  challengeId: string,
  opts: {
    senderMove: JankenMove;
    challengeType: JankenChallengeType;
    forceNativeShare?: boolean;
  },
): Promise<void> {
  const { senderMove, challengeType, forceNativeShare } = opts;
  const senderDisplayName = getPlayerProfile(
    user(),
    user().publicProfile?.name,
    undefined,
  ).name;

  if (!forceNativeShare && isShareTargetPickerAvailable()) {
    // LINE path: creative is resolved fresh each share (no caching).
    const creativeText = getCreativeText('janken_challenge');
    const species = getCurrentEvolution(user())?.species ?? 'starball';

    const lineSubFeature = getJankenShareSubFeature('line', challengeType);
    await openJankenLineSharePicker(
      challengeId,
      senderDisplayName,
      species,
      creativeText,
      lineSubFeature,
    );

    track('JankenSend', {
      challengeId,
      senderMove,
      shareMethod: 'line',
      $creativeAssetID: `janken_${species}_${creativeText.id}`,
      $creativeTextID: creativeText.id,
      $creativeCTA: creativeText.cta,
    });
    return;
  }

  // Native path: generateJankenShareURL caches the URL and its creative data
  // per challengeId, so repeat shares report the same creative that was
  // actually baked into the shared image.
  const nativeSubFeature = getJankenShareSubFeature('native', challengeType);
  const data = await withLoading(
    () =>
      generateJankenShareURL(challengeId, senderDisplayName, nativeSubFeature),
    {
      feature: Feature.JANKEN._,
      subFeature: nativeSubFeature,
    },
  );

  if (!data) {
    return openPopupPromise('popupError', {
      message: i18n('error.general.tryAgain'),
    });
  }

  await sendInviteURL(data.url);

  track('JankenSend', {
    challengeId,
    senderMove,
    shareMethod: 'native',
    $creativeAssetID: data.creativeAssetID,
    $creativeTextID: data.creativeTextID,
    $creativeCTA: data.creativeCTA,
  });
}

/** Open LINE Share Target Picker with a janken challenge flex message. */
async function openJankenLineSharePicker(
  challengeId: string,
  senderDisplayName: string,
  species: SpeciesChatbotType,
  creativeText: CreativeText,
  subFeature: JankenShareSubFeature,
): Promise<void> {
  const { encodedPayload, analyticsEventProperties } =
    platform.generateEntryPayload({
      ...makePayload('SHARE'),
      channel: 'SHARE' as AnalyticsProperties.Channel,
      feature: Feature.JANKEN._,
      subFeature,
      $subFeature: subFeature,
      $creativeAssetID: `janken_${species}_${creativeText.id}`,
      $creativeTextID: creativeText.id,
      $creativeCTA: creativeText.cta,
      creativeCTA: creativeText.cta,
      webReferrer: user().id,
      challengeId,
      senderSpecies: species,
      senderDisplayName,
    });
  const petAssetKey = getPetAssetKey(species, 'happy');

  const flexMessage = createJankenChallengeFlexMessage({
    text: creativeText.text,
    altText: creativeText.text,
    cta: creativeText.cta,
    bgUrl: StateObserver.replicant.getChatbotAssetUrl('janken/bg'),
    signsUrl: StateObserver.replicant.getChatbotAssetUrl('janken/invite'),
    petUrl: StateObserver.replicant.getChatbotAssetUrl(petAssetKey),
    petAspectRatio: speciesAssetsRatio[petAssetKey] ?? '1:1',
    buttonUrl: StateObserver.replicant.getChatbotAssetUrl(
      'janken/green_button',
    ),
    payload: {
      ...encodedPayload,
      alternativeWebIABBucket: 'enabled_mini_app' as const,
    },
  });

  await openShareTargetPicker(flexMessage, analyticsEventProperties);
}

/**
 * Cached native share result. Stores the invite URL alongside the creative
 * properties that were baked into the shared image, so repeat shares for the
 * same challengeId report consistent creative IDs in analytics.
 */
type NativeShareResult = {
  url: NonNullable<Awaited<ReturnType<typeof generateInviteURL>>>;
  creativeAssetID: string;
  creativeTextID: string;
  creativeCTA: string;
};

const shareURLCache = new Map<string, NativeShareResult>();

/** @internal Exposed for tests. */
export function clearJankenShareCache() {
  shareURLCache.clear();
}

/** Generate a playco.click short URL for native share with composite image. Returns null on failure. */
async function generateJankenShareURL(
  challengeId: string,
  senderDisplayName: string,
  subFeature: JankenShareSubFeature,
): Promise<NativeShareResult | null> {
  const cached = shareURLCache.get(challengeId);
  if (cached) return cached;

  const species = getCurrentEvolution(user())?.species ?? 'starball';
  const locale = StateObserver.getLocale() as 'en' | 'ja';
  const creativeText = getCreativeText('janken_challenge');

  let imageUrl: string;
  try {
    // Compose a composite image matching the LINE flex message layout.
    const creative = await createJankenChallengeCreative({
      species,
      text: creativeText.text,
      cta: creativeText.cta,
    });

    // Upload to S3 and get a public URL for the link preview.
    // fileNamePrefix includes text variant so each gets its own cached image.
    const assetId = await StateObserver.replicant.uploadUserAsset(
      creative.image,
      `janken_${species}_${creativeText.id}`,
    );
    imageUrl = StateObserver.replicant.getUserAssetUrl(assetId);
  } catch (error) {
    captureGenericError('Failed to generate Janken share image', error, {
      extra: { challengeId, species, locale },
    });
    return null;
  }

  const url = await generateInviteURL({
    creativeAsset: {
      id: `janken_${species}_${creativeText.id}`,
      image: imageUrl,
    },
    userAsset: true,
    creativeText,
    data: {
      feature: Feature.JANKEN._,
      $subFeature: subFeature,
      challengeId,
      senderSpecies: species,
      senderDisplayName,
    },
  });

  if (!url) return null;

  const result: NativeShareResult = {
    url,
    creativeAssetID: `janken_${species}_${creativeText.id}`,
    creativeTextID: creativeText.id,
    creativeCTA: creativeText.cta,
  };
  shareURLCache.set(challengeId, result);
  return result;
}
